import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import ResponsiveDialog from "./ResponsiveDialog";
import {Button, ButtonBase, IconButton} from "@material-ui/core";
import {useIdb} from 'react-use-idb'
import Grid from "@material-ui/core/Grid";
import snackbar from "../snackbar";
import Edit from "@material-ui/icons/Edit";
import useLocalStorage from "@rehooks/local-storage";
import SignaturePad from "./SignaturePad";
import Slider from '@material-ui/core/Slider';
import {useTranslation} from "react-i18next";
import {GenerateInputs} from "./GenericInputField";
import {apiPost} from "../api";
import {useAPI} from "../stateHelpers";
import {ValidatorForm} from "react-form-validator-core";
import * as lodash from "lodash";
import {useCurrentState} from "../globalState";

function TabContainer(props) {
    return (
        <Typography component="div">
            {props.children}
        </Typography>
    );
}

TabContainer.propTypes = {
    children: PropTypes.node.isRequired,
};

const styles = theme => ({
    root: {
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
    },
});

const penColors = [
    "#000000",
    "#d8181f",
    "#5b5baf"
];

function SimpleTabs(props) {

    let [currentRef, setRef] = useState(null);
    let [value, setValue] = useState(0);
    let [stored, setStored] = useIdb("signatures", []);
    const {classes} = props;
    let [signature, setSignature] = useState({});
    let fontsJSON = useAPI("fonts");
    let [initialSetup, setInitialSetup] = useState(false);
    let [penColor, setPenColor] = useLocalStorage("penColor");
    penColor = "#000000";
    let [dotSize, setDotSize] = useLocalStorage("dotSize");
    dotSize = 1;
    let [fontChoice, setFontChoice] = useLocalStorage("fontChoice");
    const {t, i18n} = useTranslation();
    const inputRef = useRef(null);
    const [appBarRef, setAppBarRef] = useState(null);
    const [currentDialogAppBarWidth, _] = useCurrentState("currentDialogAppBarWidth");


    let getSenders = () => {
        return (fontsJSON.data || []).map((item) => {
            return {label: item.name, key: item.key};
        });
    };

    let inputs = GenerateInputs({
        writer: {
            type: 'plain_textarea',
            required: true,
            defaultValue: "",
            staticStyles: {
                fontSize: 50
            }
        },
        fonts: {
            label: t("Font"),
            type: 'select',
            selection: getSenders(),
            required: true,
            validators: ['required'],
            errorMessages: ['this field is required']
        },
        signature: {
            label: false,
            defaultValue: null,
            multi: false,
            type: 'file',
            help: t("upload_signature_help")
        }
    });

    if (!fontsJSON.isLoading && !inputs.fonts.value && fontsJSON.data) {
        inputs.fonts.setValue(fontsJSON.data[0].key);
    }

    useEffect(() => {
        if (inputs.fonts.value) {
            if (fontChoice !== inputs.fonts.value) {
                setFontChoice(inputs.fonts.value);
            }
            let fam = lodash.find(getSenders(), {key: inputs.fonts.value});
            if (!fam) {
                fam = "";
            } else {
                fam = fam.label;
            }
            inputs.writer.setCustomStyles({
                color: penColor,
                fontFamily: "'" + fam + "'"
            });
        }

        if (currentRef) {
            currentRef.font = inputs.fonts.value;
            currentRef.fontColor = penColor;
        }

    }, [inputs.fonts.value, penColor, currentRef, getSenders().length]);

    useEffect(() => {
        if (inputs.writer.value && inputs.writer.value.trim().length) {
            window.formDirty = t("leave_without_saving");
            props.setActionButtonDisabled(false);
        } else {
            props.setActionButtonDisabled(true);
            window.formDirty = null;
        }
        if (currentRef) {
            currentRef.text = inputs.writer.value;
        }

    }, [inputs.writer.value, currentRef, props.setActionButtonDisabled]);

    useEffect(() => {
        if (inputs.signature.value) {
            apiPost("signature_to_base64", inputs.toFormData()).then((data) => {
                let neww = {
                    recorded: Date.now(),
                    path: 'data:' + data.data.type + ';base64,' + data.data.base_64
                };
                setSignature(neww);
                setValue(0);
                props.setActionButtonDisabled(false);
                snackbar(t("ui_pad_msg_saved"));
                inputs.signature.setValue(null);
            }, () => {
                snackbar(t("ui_pad_unable_to_upload"));
                inputs.signature.setValue(null);
            });
        }
    }, [props.setActionButtonDisabled, inputs.signature.value]);

    if (!penColor) {
        penColor = penColors[0];
    }

    useEffect(() => {
        if (signature.path && currentRef) {
            currentRef.clear();
            if (signature.svgx) {
                apiPost("convert_svg_to_png", {svg: signature.svg}).then((res) => {
                    currentRef.fromDataURL('data:' + res.data.type + ';base64,' + res.data.base_64, {
                        width: currentRef.canvas.width,
                        height: currentRef.canvas.height
                    });
                }, () => {
                    snackbar(t("failed_loading_saved_signature"));
                });
            } else {
                currentRef.fromDataURL(signature.path, {
                    width: currentRef.canvas.width,
                    height: currentRef.canvas.height
                });
            }
        }
    }, [signature, currentRef]);

    useEffect(() => {
        if (currentRef) {
            currentRef.penColor = penColor;
            if (currentRef) {
                currentRef.minWidth = dotSize;
                currentRef.maxWidth = dotSize * 5;
            }
        }

    }, [penColor, currentRef]);


    useEffect(() => {
        if (currentRef) {
            currentRef.tab = value;
        }
    }, [value, currentRef]);

    let handleDotSize = useCallback((event, val) => {
        setDotSize(val);

        if (currentRef) {
            currentRef.minWidth = val;
            currentRef.maxWidth = val * 5;
        }

    }, [currentRef, setDotSize]);

    let onDrawed = useCallback((pad) => {
        window.formDirty = t("leave_without_saving");

        if (pad) {
            let sizeof = pad.toDataURL().length;
            let total = 0;
            pad._data.forEach((v) => {
                total += v.points.length;
            });
            props.setActionButtonDisabled(total < 15);
        }
    }, [props.setActionButtonDisabled]);

    let save = () => {
        let neww = {
            recorded: Date.now(),
            svg: currentRef.toSVG(),
            path: currentRef.toDataURL()
        };
        setStored([neww, ...stored]);

        setSignature(neww);

        snackbar(t("ui_pad_msg_saved"));
    };

    let deleteSignature = () => {
        setStored(stored.filter((stor) => stor.recorded !== signature.recorded));
        clear();
    };

    let clear = () => {
        currentRef.clear();
        setSignature({cleared: true});
        props.setActionButtonDisabled(true);
    };

    let getAppbarHeight = useCallback(() => {
        if (window.innerWidth >= 1024) {
            return undefined;
        }
        return appBarRef ? appBarRef.clientHeight + currentDialogAppBarWidth : 0;
    }, [appBarRef, currentDialogAppBarWidth]);

    useEffect(() => {

        if (initialSetup) {
            return;
        }

        if (fontChoice && !inputs.fonts.value) {
            inputs.fonts.setValue(fontChoice);
        }

        if (!(signature.path || signature.cleared) && props.getImage()) {
            let info = props.getInfo();
            if (info && info.placeholder) {
                let placeholder = info.placeholder;
                let text = placeholder._text || placeholder.text;
                if (text && text !== "") {
                    inputs.fonts.setValue(placeholder._text_font || placeholder.text_font);
                    setPenColor(placeholder._text_color || placeholder.text_color);
                    inputs.writer.setValue(placeholder._text || placeholder.text);
                    setInitialSetup();
                    setValue(1);
                    return;
                }
            }

            setSignature({
                path: props.getImage()
            });
            setInitialSetup();
        }

    }, [initialSetup]);

    let ww = 800;
    let padww = 798;
    let padhh = 330;
    let height = 340;

    if (window.innerWidth <= 1024) {
        let windowWidth = window.innerWidth;
        let windowHeight = window.innerHeight;
        ww = windowWidth - 2;
        height = windowHeight - 2;
        height = undefined;
        padww = windowWidth - 4;
        padhh = (windowWidth / 2.3) - 4;
    }
    if (!dotSize) {
        dotSize = 1;
    }
    dotSize = parseFloat(dotSize);

    let colorPicker = <div style={{marginLeft: 10}}>
        <Grid container
              direction="row"
              justify="flex-end"
              alignItems="flex-start">
            {penColors.map((cc, index) => <Grid key={index} item>
                <div onClick={() => setPenColor(cc)}
                     className={"color-circle " + (cc === penColor ? 'active' : '')}
                     style={{backgroundColor: cc}}/>
            </Grid>)}
        </Grid>
    </div>;

    let setSignatureRef = useCallback((ref) => {
        props.setData(ref);
        if (ref) {
            setRef(ref);
        }
    }, [props]);

    let submit = useCallback(() => {
    }, []);

    return (
        <ValidatorForm ref={inputRef} onSubmit={submit} noValidate autoComplete="off">
            <div style={{minWidth: ww, minHeight: height}} className={classes.root + ' signature-tabs'}>
                <AppBar innerRef={(t) => setAppBarRef(t)} style={{top: currentDialogAppBarWidth}}
                        position={window.innerWidth >= 1024 ? 'static' : 'fixed'}>
                    <Tabs centered={true} value={value} onChange={(event, nv) => setValue(nv)}>
                        <Tab label={t("ui_pad_draw")}/>
                        <Tab label={t("ui_pad_type")}/>
                        <Tab label={t("ui_pad_upload")}/>
                        <Tab label={t("ui_pad_saved")}/>
                    </Tabs>
                </AppBar>
                <div className={'tab-item'}
                     style={{display: value !== 0 ? 'none' : undefined, paddingTop: getAppbarHeight()}}>
                    <div className={"spaced-after"}>
                        <Grid container
                              direction="row"
                              justify="space-between"
                              alignItems="flex-start">
                            <Grid item>
                                <Typography color={"textSecondary"}
                                            variant={"subtitle1"}>{t("draw_your_signature_here")}</Typography>
                            </Grid>
                            <Grid item>
                                <Grid container
                                      direction="row"
                                      alignItems="center"
                                      justify="flex-start">

                                    <Grid item>
                                        <Button disabled={props.actionButtonDisabled} onClick={() => {
                                            save()
                                        }}>{t("ui_pad_save")}</Button>
                                    </Grid>
                                    <Grid item>
                                        <Button onClick={clear}>{t("ui_pad_clear")}</Button>
                                    </Grid>

                                    {signature.recorded ? <Grid item>
                                        <Button onClick={() => {
                                            deleteSignature();
                                        }}>{t("ui_pad_delete")}</Button>
                                    </Grid> : null}
                                </Grid>
                            </Grid>
                        </Grid>
                    </div>
                    <div className={'unselectable'} style={{
                        width: padww,
                        height: padhh,
                        borderTop: '1px solid #EEE',
                        borderBottom: '1px solid #EEE'
                    }}>
                        <SignaturePad ref={setSignatureRef}
                                      minWidth={dotSize}
                                      onEnd={onDrawed}
                                      maxWidth={dotSize * 5}
                                      redrawOnResize={true}
                                      width={padww - 2}
                                      height={padhh - 2}/>
                    </div>
                </div>
                <div className={'tab-item'}
                     style={{display: value !== 1 ? 'none' : undefined, paddingTop: getAppbarHeight()}}>
                    <div>

                        <div className={"spaced-out"}>
                            <Grid container
                                  direction="row"
                                  alignItems={"center"}
                                  alignContent={"center"}
                                  justify="flex-start">
                                <Grid item>
                                    {inputs.fonts.field}
                                </Grid>
                            </Grid>
                        </div>

                        <div className={"spaced-after"}>
                            <div>
                                <Typography color={"textSecondary"}
                                            variant={"subtitle1"}>{t("type_your_signature_here")}</Typography>
                            </div>
                            {inputs.writer.field}
                        </div>

                    </div>
                </div>
                <div className={'tab-item'}
                     style={{display: value !== 2 ? 'none' : undefined, paddingTop: getAppbarHeight()}}>
                    <div>
                        <div style={{padding: 20}}>
                            <Grid container
                                  direction="row"
                                  justify="center"
                                  alignItems="center"
                            >

                                <Grid item>
                                    {inputs.signature.field}
                                </Grid>
                            </Grid>
                        </div>
                    </div>
                </div>
                <div className={'tab-item'}
                     style={{display: value !== 3 ? 'none' : undefined, paddingTop: getAppbarHeight()}}>
                    <div>
                        {stored.map((item, index) => {
                            return <ButtonBase key={index} onClick={() => {
                                setValue(0);
                                setSignature(item);
                                props.setActionButtonDisabled(false);
                            }}>
                                <img style={{width: 100, border: '1px solid #CCC'}} src={item.path} alt={""}/>
                            </ButtonBase>
                        })}
                    </div>
                </div>
            </div>
        </ValidatorForm>
    );
}

SimpleTabs.propTypes = {
    classes: PropTypes.object.isRequired,
};

SimpleTabs = withStyles(styles)(SimpleTabs);

function SignaturePadDialog(props) {
    let {action, ...other} = props;
    let [data, setData] = useState(null);
    let [actionButtonDisabled, setActionButtonDisabled] = useState(true);
    return <ResponsiveDialog title={false} actionButtonDisabled={actionButtonDisabled} onCancel={() => {
        setActionButtonDisabled(true);
    }} action={() => {
        let t = action(data);
        setActionButtonDisabled(true);
        return t;
    }}
                             fullScreen={window.innerWidth <= 1024} {...other}>
        <SimpleTabs getInfo={props.getInfo} actionButtonDisabled={actionButtonDisabled}
                    setActionButtonDisabled={setActionButtonDisabled} getImage={props.getImage}
                    setData={setData}/>
    </ResponsiveDialog>
}

export default SignaturePadDialog;

